<template><div><dt-notice kind="warning" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Before using border color utilities, first consider <a href="/design/colors/palette/#borders" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">semantic border colors</a>.
</dt-notice>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bc-{color}</code> to set an element's border color.</p>
<code-example source-code='&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-subtle&quot;&gt;Subtle&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-default&quot;&gt;Default&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-moderate&quot;&gt;Moderate&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-bold&quot;&gt;Bold&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical&quot;&gt;Critical&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-success&quot;&gt;Success&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-warning&quot;&gt;Warning&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200" :direction="{ 'default': 'column', 'md': 'row' }" class="d-fw-wrap">
  <div class="d-p-200 d-ba d-baw2 d-bc-subtle">Subtle</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-default">Default</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-moderate">Moderate</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-bold">Bold</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical">Critical</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-success">Success</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-warning">Warning</div>
</dt-stack>
</code-example>
<h2 id="hover" tabindex="-1" class="d-docsite--header-2">Hover <a class="header-anchor d-link d-docsite--link d-link" href="#hover" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>h:d-bc-{color}</code> to change an element's border color spot on <code v-pre>:hover</code>.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 h:d-bc-moderate&quot;&gt;
  Hover over me
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 h:d-bc-critical&quot;&gt;
  Hover over me
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="200">
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 h:d-bc-moderate">
    Hover over me
  </dt-button>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 h:d-bc-critical">
    Hover over me
  </dt-button>
</dt-stack>
</code-example>
<h2 id="focus" tabindex="-1" class="d-docsite--header-2">Focus <a class="header-anchor d-link d-docsite--link d-link" href="#focus" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>f:d-bc-{color}</code> to change an element's border color when in <code v-pre>:focus</code> or <code v-pre>:focus-within</code> states.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 f:d-bc-moderate&quot;&gt;
  Focus me
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 f:d-bc-critical&quot;&gt;
  Focus me
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="200">
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 f:d-bc-moderate">
    Focus me
  </dt-button>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 f:d-bc-critical">
    Focus me
  </dt-button>
</dt-stack>
</code-example>
<h2 id="focus-visible" tabindex="-1" class="d-docsite--header-2">Focus Visible <a class="header-anchor d-link d-docsite--link d-link" href="#focus-visible" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>fv:d-bc-{color}</code> to change an element's border color when in <code v-pre>:focus-visible</code> state [only when focused by keyboard]
.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 fv:d-bc-moderate&quot;&gt;
  Keyboard focus me
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 fv:d-bc-critical&quot;&gt;
  Keyboard focus me
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="200">
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 fv:d-bc-moderate">
    Keyboard focus me
  </dt-button>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 fv:d-bc-critical">
    Keyboard focus me
  </dt-button>
</dt-stack>
</code-example>
<h2 id="changing-opacities" tabindex="-1" class="d-docsite--header-2">Changing Opacities <a class="header-anchor d-link d-docsite--link d-link" href="#changing-opacities" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bco{n}</code> to change the border color opacity value.</p>
<code-example source-code='&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco100&quot;&gt;100%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco99&quot;&gt;99%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco95&quot;&gt;95%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco90&quot;&gt;90%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco75&quot;&gt;75%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco50&quot;&gt;50%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco25&quot;&gt;25%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco10&quot;&gt;10%&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-ba d-baw2 d-bc-critical d-bco0&quot;&gt;0%&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200" :direction="{ 'default': 'column', 'md': 'row' }">
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco100">100%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco99">99%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco95">95%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco90">90%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco75">75%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco50">50%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco25">25%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco10">10%</div>
  <div class="d-p-200 d-ba d-baw2 d-bc-critical d-bco0">0%</div>
</dt-stack>
</code-example>
<p class="d-docsite--paragraph">You can also change the border color opacity value on <code v-pre>:hover</code>
, <code v-pre>:focus</code>, <code v-pre>:focus-visible</code> by using the respective <code v-pre>h:d-bco{n}</code>, <code v-pre>f:d-bco{n}</code>, <code v-pre>fv:d-bco{n}</code> prefixes.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 d-bc-critical h:d-bco50&quot;&gt;
  Hover me to see 50%
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 d-bc-critical f:d-bco50&quot;&gt;
  Focus me with mouse to see 50%
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-ba d-baw2 d-bc-critical fv:d-bco50&quot;&gt;
  Focus me via keyboard to see 50%
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="200"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 d-bc-critical h:d-bco50">
    Hover me to see 50%
  </dt-button>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 d-bc-critical f:d-bco50">
    Focus me with mouse to see 50%
  </dt-button>
  <dt-button kind="unstyled" class="d-p-200 d-ba d-baw2 d-bc-critical fv:d-bco50">
    Focus me via keyboard to see 50%
  </dt-button>
</dt-stack>
</code-example>
<dt-notice kind="info" title="Tip" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Prefer using the <a href="/components/mode-island.html#inverting" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">v-dt-mode directive</a> with base utility classes instead of <code v-pre>-inverted</code> variants. For example, use <code v-pre>&lt;div v-dt-mode:invert class=&quot;d-bc-critical&quot;&gt;</code> instead of <code v-pre>&lt;div class=&quot;d-bc-critical-inverted&quot;&gt;</code>.
</dt-notice>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<new-utility-class-table :classes="borderColors">
  <template #example="{ className }">
    <div :class="['d-d-inline-flex', {'d-bgc-contrast': className.endsWith('inverted')}]" >
      <div
        class="d-fl-shrink0 d-m-50 d-size-75 d-bar-300 d-ba d-baw2"
        :class="className"
      />
    </div>
  </template>
</new-utility-class-table>
</div></template>


<script setup>
  import { inject } from 'vue';
  import { extractUtilityClasses } from '@utilities';

  const utilityClassDocs = inject('utilityClassDocs');
  const borderColors = extractUtilityClasses(utilityClassDocs, 'd-bc-');
</script>